import { formatValue, getAddedOrDeletedMessage, getValClassName, getValueLabel, renderEdition } from './common';

import type { ReactNode } from 'react';
import type { ChangelogDiff, ChangelogRule, FormatterContext } from './types';

export const AUDIENCES_ORDER = 1200;

type AudienceList = {
  logic?: unknown;
  name?: unknown;
  segments?: unknown;
  type?: unknown;
};

function toAudienceList(value: unknown): AudienceList {
  return value && typeof value === 'object' ? (value as AudienceList) : {};
}

/** "Audience list 2": lists are an array, identified by 1-based position */
function getListLabel(position: unknown, { formatMessage }: FormatterContext): string {
  return `${formatMessage({ defaultMessage: 'Audience list', id: 'changelogModal.message.audienceList' })} ${
    Number(position) + 1
  }`;
}

/** `"Audience List" (included, AND): segment a, segment b` (stored lists may only keep their name) */
function describeList(value: unknown): string {
  const { logic, name, segments, type } = toAudienceList(value);
  const details = [type, logic].filter(Boolean).join(', ');
  const segmentNames = Array.isArray(segments) ? getValueLabel(segments) : undefined;
  return [
    `"${formatValue(name)}"`,
    details && ` (${details})`,
    segmentNames && segmentNames !== '-' && `: ${segmentNames}`
  ]
    .filter(Boolean)
    .join('');
}

function renderListAddedOrDeleted(added: boolean, list: unknown, { formatMessage }: FormatterContext): ReactNode {
  return (
    <div className={getValClassName(added ? 'add' : 'delete')}>
      <b>{formatMessage({ defaultMessage: 'Audience list', id: 'changelogModal.message.audienceList' })}</b>
      {` ${getAddedOrDeletedMessage(added, formatMessage)} `}
      {describeList(list)}
    </div>
  );
}

function renderSegmentAddedOrDeleted(change: ChangelogDiff, context: FormatterContext): ReactNode {
  const added = change.item?.kind === 'N';
  const segment = added ? change.item?.rhs : change.item?.lhs;
  return (
    <div className={getValClassName(added ? 'add' : 'delete')}>
      <b>{getListLabel(change.path?.[3], context)}</b>
      {` ${context.formatMessage({ defaultMessage: 'segment', id: 'changelogModal.message.segment' })} `}
      {`${getAddedOrDeletedMessage(added, context.formatMessage)}: `}
      <b>{formatValue(segment)}</b>
    </div>
  );
}

export const audiencesRules: ChangelogRule[] = [
  {
    // List added or removed from the lists array
    format: (change, context) =>
      renderListAddedOrDeleted(change.item?.kind === 'N', change.item?.rhs ?? change.item?.lhs, context),
    order: AUDIENCES_ORDER,
    regex: /^A\.targeting\.audience\.lists$/,
    section: 'audiences'
  },
  {
    // List added or removed at a given position
    format: (change, context) => renderListAddedOrDeleted(change.kind === 'N', change.rhs ?? change.lhs, context),
    order: AUDIENCES_ORDER,
    regex: /^[ND]\.targeting\.audience\.lists\.\d+$/,
    section: 'audiences'
  },
  {
    format: renderSegmentAddedOrDeleted,
    order: AUDIENCES_ORDER + 1,
    regex: /^A\.targeting\.audience\.lists\.\d+\.segments$/,
    section: 'audiences'
  },
  {
    // Segments are an array: removing one shifts the next ones, only their name is relevant
    format: (change, context) =>
      renderEdition(
        `${getListLabel(change.path?.[3], context)} ${context.formatMessage({
          defaultMessage: 'segment',
          id: 'changelogModal.message.segment'
        })}`,
        formatValue(change.lhs),
        formatValue(change.rhs),
        context
      ),
    order: AUDIENCES_ORDER + 1,
    regex: /^E\.targeting\.audience\.lists\.\d+\.segments\.\d+\.name$/,
    section: 'audiences'
  },
  {
    format: (change, context) =>
      renderEdition(
        `${getListLabel(change.path?.[3], context)} ${String(change.path?.[4])}`,
        formatValue(change.lhs),
        formatValue(change.rhs),
        context
      ),
    order: AUDIENCES_ORDER + 2,
    regex: /^[EN]\.targeting\.audience\.lists\.\d+\.(active|distribution|logic|name|type)$/,
    section: 'audiences'
  }
];
